import { Button } from '../../components/Button'
import { Logo } from '../../components/Logo'
import { Text } from '../../components/Text'

export type FeatureCardProps = {
  /** Heading rendered over the image (see `titlePosition`). */
  title: string
  /** Label for the CTA (e.g. "Complete Application"). Omit (with `onAction`) to hide the button. */
  ctaLabel?: string
  /** Invoked when the CTA is pressed (e.g. navigate to the application). */
  onAction?: () => void
  /** Where the heading sits over the image. Defaults to `top`. */
  titlePosition?: 'top' | 'bottom'
  /**
   * Background image URL. An empty string renders a branded placeholder panel
   * (matching Carousel) so the layout reads correctly before art lands.
   */
  imageSrc?: string
  /** Alt text / aria-label for the background image. Falls back to `title`. */
  imageAlt?: string
}

/**
 * Single promotional card sized to match the Carousel (h-[200px], rounded-lg):
 * a full-bleed background image with a heading top-left and a small primary CTA
 * bottom-left, over a dark scrim that keeps both legible on photography.
 *
 * Fully prop-driven so the same surface can front different campaigns. The CTA
 * is presentational — the parent supplies `onAction` (e.g. route navigation).
 */
export function FeatureCard({
  title,
  ctaLabel,
  onAction,
  titlePosition = 'top',
  imageSrc = '',
  imageAlt,
}: FeatureCardProps) {
  return (
    <section className="relative h-[200px] overflow-hidden rounded-lg bg-yellow-50">
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={imageAlt ?? title}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      ) : (
        <div
          role="img"
          aria-label={imageAlt ?? title}
          className="flex h-full w-full items-center justify-center bg-gradient-to-br from-yellow-50 to-yellow-200"
        >
          <Logo width={72} height={27} className="opacity-30" />
        </div>
      )}

      {/* Scrim: darkest at top and bottom so the heading and CTA stay legible. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/55" />

      <Text
        as="h2"
        variant="h3"
        weight="bold"
        className={`absolute inset-x-4 text-white drop-shadow ${
          titlePosition === 'bottom' ? 'bottom-4' : 'top-4'
        }`}
      >
        {title}
      </Text>

      {ctaLabel && onAction ? (
        <Button size="sm" className="absolute bottom-4 left-4" onClick={onAction}>
          {ctaLabel}
        </Button>
      ) : null}
    </section>
  )
}
